
import { getCurrentPosition } from './getCurrentPosition';
import { isGeolocationSupported } from './getCurrentPosition';
import { cachePosition } from './positionCache';
import { checkGeolocationPermission } from './permissions';
import { getCachedPosition } from './positionCache';
import { toast } from 'sonner';
import { isSSR } from '../isSSR';

// Track if initialization is already in progress
let initializationInProgress = false;
let retryCount = 0;
const MAX_RETRIES = 2;
let isAlreadyInitialized = false;

// Initialize geolocation at app startup
export const initGeolocation = async (forceRetry = false): Promise<GeolocationPosition | null> => {
  // Skip in SSR mode
  if (isSSR()) {
    console.log("Geolocation initialization skipped in SSR mode");
    return null;
  }
  
  // Avoid multiple initializations
  if (isAlreadyInitialized && !forceRetry) {
    console.log("Geolocation already initialized, skipping");
    return null;
  }

  // Reset retry count if forced
  if (forceRetry) {
    retryCount = 0;
  }

  // Prevent multiple initializations
  if (initializationInProgress && !forceRetry) {
    console.log("Geolocation initialization already in progress");
    return null;
  }

  initializationInProgress = true;

  try {
    if (!isGeolocationSupported()) {
      console.error("La geolocalizzazione non è supportata dal tuo browser");
      initializationInProgress = false;
      return null;
    }

    // First check if we have a valid cached position
    const cachedPosition = getCachedPosition();
    if (cachedPosition) {
      console.log("Using cached position during initialization");
      isAlreadyInitialized = true;
      
      // In background, try to refresh position after a delay
      setTimeout(() => {
        try {
          getCurrentPosition({
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 0,
            showSuccessToast: false
          }).then(position => {
            cachePosition(position);
            console.log("Background position update successful");
          }).catch(err => {
            console.log("Background position update failed:", err);
          });
        } catch (e) {
          console.error("Error in background position update:", e);
        }
      }, 5000);
      
      initializationInProgress = false;
      return cachedPosition;
    }

    // Check permission state
    const permission = await checkGeolocationPermission();
    console.log("Geolocation permission status:", permission);
    
    // If permission is granted, get position
    if (permission === "granted") {
      try {
        // Use less accuracy on initial requests to improve success rate
        const position = await getCurrentPosition({
          enableHighAccuracy: false, // Start with low accuracy for faster initial response
          timeout: 20000,
          maximumAge: 300000, // Allow positions up to 5 minutes old
          showSuccessToast: false
        });
        
        // Reset retry count on success
        retryCount = 0;
        isAlreadyInitialized = true;
        
        // Save to cache
        cachePosition(position);
        
        // Try to get a more accurate position in the background after a delay
        setTimeout(() => {
          getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 0,
            showSuccessToast: false
          }).then(accuratePosition => {
            cachePosition(accuratePosition);
            console.log("High-accuracy position update successful");
          }).catch(err => {
            console.log("High-accuracy position update failed:", err);
          });
        }, 5000);
        
        initializationInProgress = false;
        return position;
      } catch (error) {
        console.error("Error during geolocation initialization:", error);
        
        // Retry with reduced accuracy if it was a timeout error
        if (error instanceof GeolocationPositionError && error.code === 3 && retryCount < MAX_RETRIES) {
          retryCount++;
          initializationInProgress = false;
          
          console.log(`Geolocation timeout, retrying (${retryCount}/${MAX_RETRIES}) with lower accuracy`);
          
          // Wait before retrying
          setTimeout(() => {
            initGeolocation(true); // Force retry
          }, 2000 * retryCount); // Increasing delay for each retry
          
          return null;
        }
        
        initializationInProgress = false;
        return null;
      }
    } else if (permission === "prompt") {
      // If permission is not yet determined, show a toast message
      toast.info("Consenti la geolocalizzazione per vedere i compro oro più vicini");
    }
    
    initializationInProgress = false;
    return null;
  } catch (error) {
    console.error("Unexpected error in geolocation initialization:", error);
    initializationInProgress = false;
    return null;
  }
};

// Auto-init flag to prevent multiple calls
let hasAutoInitialized = false;

// Call this function on app mount with a delay
export const autoInitGeolocation = () => {
  if (!isSSR() && !hasAutoInitialized) {
    hasAutoInitialized = true;
    setTimeout(() => {
      initGeolocation().catch(error => {
        console.error("Auto-init geolocation error:", error);
      });
    }, 2000); // Give the app time to load before requesting position
  }
};
